import axios, {AxiosError} from 'axios'
import {API_URL} from '../../../../../../_metronic/helpers'
import {InvoicePrintModel, PrintOrientation} from './_models'

// The public print page reads without the panel's session: no cookie is sent and none of the panel's
// interceptors run, so a visitor with an expired session is never logged out by this page and a
// signed-in admin sees exactly what the buyer sees.
const publicClient = axios.create({withCredentials: false, timeout: 30000})

export type InvoicePrintErrorKind = 'notFound' | 'rateLimited' | 'unavailable' | 'network'

export class InvoicePrintRequestError extends Error {
  readonly kind: InvoicePrintErrorKind
  readonly retryAfterSeconds?: number

  constructor(kind: InvoicePrintErrorKind, message: string, retryAfterSeconds?: number) {
    super(message)
    this.kind = kind
    this.retryAfterSeconds = retryAfterSeconds
  }
}

const DEFAULT_MESSAGES: Record<InvoicePrintErrorKind, string> = {
  notFound:
    'صورتحسابی با این لینک پیدا نشد. لینک را کامل و بدون تغییر باز کنید یا از صادرکنندهٔ صورتحساب لینک تازه بخواهید.',
  rateLimited: 'تعداد درخواست‌ها از این نشانی بیش از حد مجاز است؛ چند دقیقه دیگر دوباره تلاش کنید.',
  unavailable: 'دریافت صورتحساب انجام نشد. چند لحظه دیگر دوباره تلاش کنید.',
  network: 'اتصال به سامانه برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.',
}

const readServerMessage = async (data: unknown): Promise<string | undefined> => {
  try {
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      const parsed = JSON.parse(await data.text())
      return typeof parsed?.message === 'string' ? parsed.message : undefined
    }
    const message = (data as {message?: unknown})?.message
    return typeof message === 'string' ? message : undefined
  } catch {
    return undefined
  }
}

export const toInvoicePrintError = async (error: unknown): Promise<InvoicePrintRequestError> => {
  const axiosError = error as AxiosError
  const status = axiosError?.response?.status
  if (!axiosError?.response) {
    return new InvoicePrintRequestError('network', DEFAULT_MESSAGES.network)
  }

  const kind: InvoicePrintErrorKind =
    status === 404 ? 'notFound' : status === 429 ? 'rateLimited' : 'unavailable'
  const retryAfter = Number(axiosError.response.headers?.['retry-after'])
  const serverMessage = await readServerMessage(axiosError.response.data)
  return new InvoicePrintRequestError(
    kind,
    serverMessage || DEFAULT_MESSAGES[kind],
    Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined
  )
}

const printUrl = (hash: string) => `${API_URL}/public/invoice-print/${encodeURIComponent(hash)}`

/**
 * The printed invoice. The server's PDF renderer opens the page with a short-lived token in the
 * address fragment (#pdfToken=…); it is passed on so the renderer is not rate-limited.
 */
export const getInvoicePrint = async (
  hash: string,
  pdfRenderToken?: string
): Promise<InvoicePrintModel> => {
  try {
    const response = await publicClient.get<{data: InvoicePrintModel}>(printUrl(hash), {
      headers: pdfRenderToken ? {Authorization: `InvoicePdf ${pdfRenderToken}`} : undefined,
    })
    return response.data.data
  } catch (error) {
    throw await toInvoicePrintError(error)
  }
}

/** The same print rendered to PDF on the server; resolves with the file and its name. */
export const downloadInvoicePrintPdf = async (
  hash: string,
  orientation: PrintOrientation | undefined,
  layoutVersion: string
): Promise<{blob: Blob; fileName: string}> => {
  try {
    const response = await publicClient.get<Blob>(`${printUrl(hash)}/pdf`, {
      responseType: 'blob',
      timeout: 180000,
      params: {orientation, layout: layoutVersion},
    })
    const disposition = String(response.headers?.['content-disposition'] || '')
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
    const fileName = match ? decodeURIComponent(match[1]) : `invoice_${hash.slice(0, 8)}.pdf`
    return {blob: response.data, fileName}
  } catch (error) {
    throw await toInvoicePrintError(error)
  }
}
